import { useState } from 'react';
import { RefreshCw, FileText, CreditCard, AlertCircle, Wallet } from 'lucide-react';
import type { Room, Resident } from '../types';
import { formatDateDDMMYYYY } from '../utils/format';
import { downloadInvoice } from '../utils/invoice';
import { getOpenCicilanSisa } from '../utils/cicilan';

interface LatePaymentsByBuildingProps {
  rooms: Room[];
}

interface LatePaymentInfo {
  roomNumber: string;
  residentName: string;
  residentPhone: string;
  residentPosition: number;
  daysLate: number;
  lastPaymentDate: string;
  nextPaymentDate: string;
  category: 'DP' | 'Pelunasan' | 'Cicilan' | 'Regular';
  paymentType: 'dp_unpaid' | 'kos_overdue' | 'cicilan_sisa';
  resident: Resident;
}

export function LatePaymentsByBuilding({ rooms }: LatePaymentsByBuildingProps) {
  const [isRefreshingDP, setIsRefreshingDP] = useState(false);
  const [isRefreshingKos, setIsRefreshingKos] = useState(false);
  const [isRefreshingCicilan, setIsRefreshingCicilan] = useState(false);

  const getLatePayments = (): {
    dpUnpaid: LatePaymentInfo[];
    kosOverdue: LatePaymentInfo[];
    cicilanSisa: LatePaymentInfo[];
  } => {
    const today = new Date();
    const dpUnpaid: LatePaymentInfo[] = [];
    const kosOverdue: LatePaymentInfo[] = [];
    const cicilanSisa: LatePaymentInfo[] = [];

    rooms.forEach(room => {
      room.residents.forEach(resident => {
        if (resident.paymentPeriods.length > 0) {
          // Check for DP yang belum dilunasi
          const dpPeriods = resident.paymentPeriods.filter(period => period.category === 'DP');
          const pelunasanPeriods = resident.paymentPeriods.filter(period => period.category === 'Pelunasan');
          
          // Jika ada DP tapi belum ada Pelunasan
          if (dpPeriods.length > 0 && pelunasanPeriods.length === 0) {
            const lastDP = dpPeriods[dpPeriods.length - 1];
            const dpDate = new Date(lastDP.dpDate || lastDP.startDate);
            const daysSinceDP = Math.floor((today.getTime() - dpDate.getTime()) / (1000 * 60 * 60 * 24));
            
            dpUnpaid.push({
              roomNumber: room.number,
              residentName: resident.name,
              residentPhone: resident.phone,
              residentPosition: resident.position,
              daysLate: daysSinceDP,
              lastPaymentDate: lastDP.paymentProofDate || lastDP.startDate,
              nextPaymentDate: lastDP.nextPaymentDate,
              category: 'DP',
              paymentType: 'dp_unpaid',
              resident: resident
            });
          }

          // Sisa cicilan terbuka (Cicilan 1 tanpa Cicilan 2 / Pelunasan penutup)
          const openSisa = getOpenCicilanSisa(resident, today);
          if (openSisa) {
            cicilanSisa.push({
              roomNumber: room.number,
              residentName: resident.name,
              residentPhone: resident.phone,
              residentPosition: resident.position,
              daysLate: openSisa.daysSince,
              lastPaymentDate: openSisa.cicilan1.paymentProofDate || openSisa.cicilan1.startDate || '',
              nextPaymentDate: openSisa.sisaDueDate,
              category: 'Cicilan',
              paymentType: 'cicilan_sisa',
              resident: resident
            });
          }

          // Check for regular kos payment overdue
          const lastPeriod = resident.paymentPeriods[resident.paymentPeriods.length - 1];
          const nextPaymentDate = new Date(lastPeriod.nextPaymentDate);
          
          if (nextPaymentDate < today) {
            const daysLate = Math.floor((today.getTime() - nextPaymentDate.getTime()) / (1000 * 60 * 60 * 24));
            
            // Skip jika sudah masuk ke DP unpaid
            const isAlreadyInDP = dpUnpaid.some(dp => 
              dp.roomNumber === room.number && dp.residentPosition === resident.position
            );
            
            if (!isAlreadyInDP) {
              kosOverdue.push({
                roomNumber: room.number,
                residentName: resident.name,
                residentPhone: resident.phone,
                residentPosition: resident.position,
                daysLate: daysLate,
                lastPaymentDate: lastPeriod.paymentProofDate || lastPeriod.startDate,
                nextPaymentDate: lastPeriod.nextPaymentDate,
                category: lastPeriod.category || 'Regular',
                paymentType: 'kos_overdue',
                resident: resident
              });
            }
          }
        }
      });
    });

    // Sort by days late (descending)
    return {
      dpUnpaid: dpUnpaid.sort((a, b) => b.daysLate - a.daysLate),
      kosOverdue: kosOverdue.sort((a, b) => b.daysLate - a.daysLate),
      cicilanSisa: cicilanSisa.sort((a, b) => b.daysLate - a.daysLate),
    };
  };

  const { dpUnpaid, kosOverdue, cicilanSisa } = getLatePayments();

  const handleRefreshDP = () => {
    setIsRefreshingDP(true);
    setTimeout(() => setIsRefreshingDP(false), 1000);
  };

  const handleRefreshKos = () => {
    setIsRefreshingKos(true);
    setTimeout(() => setIsRefreshingKos(false), 1000);
  };

  const handleRefreshCicilan = () => {
    setIsRefreshingCicilan(true);
    setTimeout(() => setIsRefreshingCicilan(false), 1000);
  };

  const renderPaymentPanel = (
    payments: LatePaymentInfo[],
    title: string,
    description: string,
    icon: React.ReactNode,
    isRefreshing: boolean,
    onRefresh: () => void,
    colorScheme: 'orange' | 'red' | 'blue',
    showPdfButton: boolean = true
  ) => {
    if (payments.length === 0) return null;

    const colors = {
      orange: {
        bg: 'bg-warning-50 dark:bg-warning-900/30',
        border: 'border-warning-200 dark:border-warning-700',
        text: 'text-warning-800 dark:text-warning-300',
        textSecondary: 'text-warning-700 dark:text-warning-400',
        badge: 'bg-warning-600 border-2 border-warning-700',
        button: 'bg-warning-600 hover:bg-warning-500 border-b-4 border-warning-700 active:border-b-0',
        cardBorder: 'border-warning-500 dark:border-warning-600',
        statusBg: 'bg-warning-100 dark:bg-warning-900/60',
        statusText: 'text-warning-800 dark:text-warning-300'
      },
      red: {
        bg: 'bg-error-50 dark:bg-error-900/30',
        border: 'border-error-200 dark:border-error-700',
        text: 'text-error-800 dark:text-error-300',
        textSecondary: 'text-error-700 dark:text-error-400',
        badge: 'bg-error-600 border-2 border-error-700',
        button: 'bg-error-600 hover:bg-error-500 border-b-4 border-error-700 active:border-b-0',
        cardBorder: 'border-error-500 dark:border-error-600',
        statusBg: 'bg-error-100 dark:bg-error-900/60',
        statusText: 'text-error-800 dark:text-error-300'
      },
      blue: {
        bg: 'bg-blue-50 dark:bg-blue-900/30',
        border: 'border-blue-200 dark:border-blue-700',
        text: 'text-blue-800 dark:text-blue-300',
        textSecondary: 'text-blue-700 dark:text-blue-400',
        badge: 'bg-blue-600 border-2 border-blue-700',
        button: 'bg-blue-600 hover:bg-blue-500 border-b-4 border-blue-700 active:border-b-0',
        cardBorder: 'border-blue-500 dark:border-blue-600',
        statusBg: 'bg-blue-100 dark:bg-blue-900/60',
        statusText: 'text-blue-800 dark:text-blue-300'
      }
    };

    const color = colors[colorScheme];

    return (
      <div className={`${color.bg} ${color.border} border-2 rounded-xl p-3 sm:p-4 mb-4 sm:mb-5`}>
        <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 mb-3 sm:mb-4">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {icon}
            <h2 className={`text-base sm:text-lg font-bold ${color.text}`}>
              {title}
            </h2>
            <span className={`${color.badge} text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-lg text-xs sm:text-sm font-bold`}>
              {payments.length}
            </span>
          </div>
          <button 
            onClick={onRefresh}
            disabled={isRefreshing}
            className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 ${color.button} text-white rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all`}
            title={`Refresh ${title.toLowerCase()}`}
          >
            <RefreshCw className={`h-3 w-3 sm:h-4 sm:w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
        
        <p className={`text-xs sm:text-sm ${color.textSecondary} mb-3 sm:mb-4`}>
          {description}
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          {payments.map((payment, index) => (
            <div
              key={`${payment.roomNumber}-${payment.residentPosition}-${index}`}
              className={`flex flex-col bg-light-surface dark:bg-dark-surface p-3 rounded-lg sm:rounded-xl cursor-pointer hover:bg-light-surfaceAlt dark:hover:bg-dark-surfaceAlt border-l-4 ${color.cardBorder} gap-2`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm sm:text-base font-bold text-light-text dark:text-dark-text truncate">
                  Kamar {payment.roomNumber}
                </div>
                <div className="text-xs sm:text-base text-light-textSecondary dark:text-dark-textSecondary truncate">
                  {payment.residentName}
                </div>
                <div className="text-[10px] sm:text-sm text-light-textMuted dark:text-dark-textMuted truncate">
                  {payment.residentPhone}
                </div>
                <div className="text-[10px] sm:text-sm text-light-textMuted dark:text-dark-textMuted mt-0.5">
                  {payment.category}
                </div>
              </div>
              
              <div className="flex flex-col gap-1 sm:gap-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                <div className={`text-[10px] sm:text-sm ${color.textSecondary} font-semibold`}>
                  {payment.paymentType === 'dp_unpaid' 
                    ? `DP: ${formatDateDDMMYYYY(payment.lastPaymentDate)}`
                    : payment.paymentType === 'cicilan_sisa'
                      ? `Sisa due: ${formatDateDDMMYYYY(payment.nextPaymentDate)}`
                      : `Tempo: ${formatDateDDMMYYYY(payment.nextPaymentDate)}`
                  }
                </div>
                {payment.paymentType === 'cicilan_sisa' && (
                  <div className="text-[10px] sm:text-xs text-light-textMuted dark:text-dark-textMuted">
                    Cicilan 1: {formatDateDDMMYYYY(payment.lastPaymentDate)}
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <div className={`text-[10px] sm:text-sm ${color.statusBg} ${color.statusText} px-2 py-0.5 rounded font-bold`}>
                    {payment.daysLate} hari
                  </div>
                  {showPdfButton && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadInvoice(payment.resident, true);
                      }}
                      className="flex items-center gap-1 bg-primary-600 hover:bg-primary-500 border-b-2 border-primary-700 active:border-b-0 text-white px-2 py-1 rounded-lg text-[10px] sm:text-xs font-semibold transition-all"
                    >
                      <FileText className="h-3 w-3" />
                      <span className="hidden sm:inline">PDF</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Panel 1: DP yang belum dilunasi */}
      {renderPaymentPanel(
        dpUnpaid,
        "DP Belum Dilunasi",
        "Daftar penghuni yang sudah membayar DP tapi belum melakukan pelunasan.",
        <CreditCard className="text-warning-600 h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />,
        isRefreshingDP,
        handleRefreshDP,
        'orange',
        false
      )}

      {/* Panel 2: Sisa cicilan belum dilunasi */}
      {renderPaymentPanel(
        cicilanSisa,
        "Sisa Cicilan Belum Dilunasi",
        "Daftar penghuni yang sudah Cicilan 1 tapi belum Cicilan 2 / pelunasan sisa. Due sisa = tanggal Cicilan 1 + 1 bulan.",
        <Wallet className="text-blue-600 h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />,
        isRefreshingCicilan,
        handleRefreshCicilan,
        'blue',
        false
      )}

      {/* Panel 3: Pembayaran Tertunggak */}
      {renderPaymentPanel(
        kosOverdue,
        "Pembayaran Tertunggak",
        "Daftar penghuni yang pembayarannya sudah melewati tanggal jatuh tempo.",
        <AlertCircle className="text-error-600 h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />,
        isRefreshingKos,
        handleRefreshKos,
        'red',
        true
      )}
    </>
  );
}
